"use client";
import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { LogOut, User, LogIn } from "lucide-react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { CurrentUserAvatar } from "../current-user-avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { jwtDecode } from "jwt-decode";
import { usePathname } from "next/navigation";
import { User as Usertype } from "@supabase/supabase-js";

export function UserNav() {
  const router = useRouter();
  const [userRole, setUserRole] = useState<string | undefined>();
  const [user, setUser] = useState<Usertype>();
  const supabase = createClient();
  const pathname = usePathname();

  useEffect(() => {
    interface AuthStateChangeData {
      event: string;
      session: {
        access_token: string;
        user: Usertype;
      } | null;
    }

    interface DecodedJWT {
      user_role?: string;
    }

    const { data } = supabase.auth.onAuthStateChange(
      async (
        event: AuthStateChangeData["event"],
        session: AuthStateChangeData["session"]
      ) => {
        if (session) {
          const jwt: DecodedJWT = jwtDecode<DecodedJWT>(session.access_token);
          const user_role = jwt?.user_role ?? "admin";
          setUser(session.user);
          setUserRole(user_role);
        } else {
          setUserRole(undefined);
        }
      }
    );

    return () => {
      data?.subscription.unsubscribe();
    };
  }, [pathname, supabase]);

  const handleSignOut = useCallback(async () => {
    const supabase = createClient();
    await supabase.auth.signOut();
    router.push("/auth/login");
  }, [router]);

  const handleSignIn = () => router.push("/login");

  return (
    <DropdownMenu>
      <TooltipProvider disableHoverableContent>
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="relative h-8 w-8 rounded-full"
              >
                <CurrentUserAvatar />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Profile</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">
              {user?.user_metadata?.full_name ?? user?.email}
            </p>
            <p className="text-xs leading-none text-muted-foreground">
              {user?.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {userRole === "admin" && (
            <DropdownMenuItem className="hover:cursor-pointer" asChild>
              <Link href="/admin/reports" className="flex items-center">
                <User className="w-4 h-4 mr-3 text-muted-foreground" />
                Admin
              </Link>
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuSeparator />
        {user ? (
          <DropdownMenuItem
            className="hover:cursor-pointer"
            onClick={handleSignOut}
          >
            <LogOut className="w-4 h-4 mr-3 text-muted-foreground" />
            Logout
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            className="hover:cursor-pointer"
            onClick={handleSignIn}
          >
            <LogIn className="w-4 h-4 mr-3 text-muted-foreground" />
            Login
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
